import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  House, Users, Student, CalendarCheck, FileText, BookOpen, 
  Notebook, Bell, ClipboardText, Gear, SignOut, List, X,
  ChartBar, Moon, Sun, CaretLeft, CaretRight, UserPlus, IdentificationCard
} from '@phosphor-icons/react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { getAlertas } from '../lib/api';

const LOGO_URL = process.env.REACT_APP_LOGO_URL || "https://customer-assets.emergentagent.com/job_cc91a7b0-9aa5-4ba1-a9e7-50b1811a744d/artifacts/w3xcvyw6_escudo%20sf%207x8.png";

const SidebarLayout = ({ children }) => {
  const { user, logout, hasPermission } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const [alertCount, setAlertCount] = useState(0);

  useEffect(() => {
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme === 'dark') {
      setDarkMode(true);
      document.documentElement.classList.add('dark');
    }
  }, []);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        const response = await getAlertas({ atendida: false });
        setAlertCount(response.data.length);
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          console.error('Error fetching alerts:', error);
        }
      }
    };
    fetchAlerts();
    const interval = setInterval(fetchAlerts, 60000);
    return () => clearInterval(interval);
  }, []);

  const toggleTheme = () => {
    setDarkMode(!darkMode);
    if (darkMode) {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    } else {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = useMemo(() => {
    if (!user) return [];
    // moduloKey identifica el módulo en modulos_permitidos (para rol "administrativo")
    const items = [
      { path: '/dashboard', icon: House, label: 'Dashboard', roles: ['administrador', 'direccion', 'prefectura', 'docente', 'administrativo'], moduloKey: 'dashboard' },
      { path: '/inscripciones', icon: UserPlus, label: 'Inscripciones', roles: ['administrador', 'direccion', 'administrativo'], moduloKey: 'inscripciones' },
      { path: '/alumnos', icon: Student, label: 'Alumnos', roles: ['administrador', 'direccion', 'prefectura', 'docente', 'administrativo'], moduloKey: 'alumnos' },
      { path: '/asistencia', icon: CalendarCheck, label: 'Asistencia', roles: ['administrador', 'direccion', 'prefectura', 'administrativo'], moduloKey: 'asistencia' },
      { path: '/expediente', icon: FileText, label: 'Expediente', roles: ['administrador', 'direccion', 'prefectura', 'administrativo'], moduloKey: 'expediente' },
      { path: '/bitacora', icon: Notebook, label: 'Bitácora', roles: ['administrador', 'direccion', 'prefectura', 'administrativo'], moduloKey: 'bitacora' },
      { path: '/patre', icon: BookOpen, label: 'PATRE', roles: ['administrador', 'direccion', 'docente', 'administrativo'], moduloKey: 'patre' },
      { path: '/alertas', icon: Bell, label: 'Alertas', roles: ['administrador', 'direccion', 'prefectura', 'docente', 'administrativo'], moduloKey: 'alertas', badge: alertCount },
      { path: '/reportes', icon: ChartBar, label: 'Reportes', roles: ['administrador', 'direccion', 'prefectura', 'administrativo'], moduloKey: 'reportes' },
      { path: '/credenciales', icon: IdentificationCard, label: 'Credencialización', roles: ['administrador', 'direccion', 'administrativo'], moduloKey: 'credencialización' },
      { path: '/usuarios', icon: Users, label: 'Usuarios', roles: ['administrador', 'direccion'] },
      { path: '/auditoria', icon: ClipboardText, label: 'Auditoría', roles: ['administrador', 'direccion'] },
      { path: '/configuracion', icon: Gear, label: 'Configuración', roles: ['administrador'] },
    ];
    return items.filter((item) => {
      if (!item.roles.includes(user.rol)) return false;
      // Filtro adicional para rol administrativo: solo módulos en modulos_permitidos
      if (user.rol === 'administrativo' && item.moduloKey) {
        const modulos = user.modulos_permitidos || [];
        return modulos.includes(item.moduloKey);
      }
      return true;
    });
  }, [user, alertCount]);

  const isActive = useCallback((path) => location.pathname === path, [location.pathname]);

  const getRolLabel = (rol) => {
    const labels = {
      administrador: 'Administrador',
      direccion: 'Dirección',
      prefectura: 'Prefectura',
      docente: 'Docente',
      administrativo: 'Administrativo'
    };
    return labels[rol] || rol;
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Mobile menu button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Button
          variant="outline"
          size="icon"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-btn"
        >
          {mobileOpen ? <X size={20} /> : <List size={20} />}
        </Button>
      </div>

      {/* Overlay */}
      {mobileOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-30"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-screen bg-card border-r border-border z-40 flex flex-col transition-all duration-300
          ${collapsed ? 'w-16' : 'w-64'}
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}
        data-testid="sidebar"
      >
        {/* Logo */}
        <div className={`p-4 border-b border-border flex items-center ${collapsed ? 'justify-center' : 'gap-3'}`}>
          <img 
            src={LOGO_URL} 
            alt="Escudo" 
            className={`${collapsed ? 'w-10 h-10' : 'w-12 h-12'} object-contain`}
          />
          {!collapsed && (
            <div className="flex-1 min-w-0">
              <h1 className="font-heading font-bold text-sm text-foreground truncate">
                Sec. Benito Juárez
              </h1>
              <p className="text-xs text-muted-foreground truncate">Sistema de Gestión</p>
            </div>
          )}
        </div>

        {/* User info */}
        {!collapsed && user && (
          <div className="p-4 border-b border-border">
            <p className="font-medium text-sm truncate">{user.nombre_completo}</p>
            <Badge variant="secondary" className="mt-1 text-xs">
              {getRolLabel(user.rol)}
            </Badge>
            {user.turno && (
              <p className="text-xs text-muted-foreground mt-1 capitalize">
                Turno {user.turno}
              </p>
            )}
          </div>
        )}

        {/* Navigation */}
        <ScrollArea className="flex-1 py-2">
          <nav className="space-y-1 px-2">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const active = isActive(item.path);
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-3 px-3 py-2 rounded-md transition-colors relative
                    ${active ? 'bg-primary/10 text-primary border-l-2 border-primary -ml-[2px] pl-[14px]' : 'hover:bg-muted text-muted-foreground hover:text-foreground'}
                    ${collapsed ? 'justify-center' : ''}`}
                  onClick={() => setMobileOpen(false)}
                  data-testid={`nav-${item.path.slice(1)}`}
                >
                  <Icon size={20} weight={active ? 'fill' : 'regular'} />
                  {!collapsed && (
                    <>
                      <span className="text-sm font-medium">{item.label}</span>
                      {item.badge > 0 && (
                        <Badge variant="destructive" className="ml-auto text-xs px-1.5 py-0">
                          {item.badge}
                        </Badge>
                      )}
                    </>
                  )}
                  {collapsed && item.badge > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-destructive rounded-full text-[10px] text-white flex items-center justify-center">
                      {item.badge > 9 ? '9+' : item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>
        </ScrollArea>

        {/* Footer */}
        <div className="p-2 border-t border-border space-y-1">
          <Button
            variant="ghost"
            size="sm"
            className={`w-full ${collapsed ? 'justify-center px-0' : 'justify-start'}`}
            onClick={toggleTheme}
            data-testid="theme-toggle"
          >
            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
            {!collapsed && <span className="ml-2 text-sm">{darkMode ? 'Modo claro' : 'Modo oscuro'}</span>}
          </Button>
          
          <Button
            variant="ghost"
            size="sm"
            className={`w-full text-destructive hover:text-destructive hover:bg-destructive/10 ${collapsed ? 'justify-center px-0' : 'justify-start'}`}
            onClick={handleLogout}
            data-testid="logout-btn"
          >
            <SignOut size={18} />
            {!collapsed && <span className="ml-2 text-sm">Cerrar sesión</span>}
          </Button>

          {/* Collapse button - desktop only */}
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-center hidden lg:flex"
            onClick={() => setCollapsed(!collapsed)}
            data-testid="collapse-sidebar"
          >
            {collapsed ? <CaretRight size={18} /> : <CaretLeft size={18} />}
          </Button>
        </div>
      </aside>

      {/* Main content */}
      <main className={`min-h-screen transition-all duration-300 ${collapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
        {/* Header */}
        <header className="sticky top-0 z-20 bg-background/80 backdrop-blur-md border-b border-border px-4 lg:px-6 py-4">
          <div className="flex items-center justify-between max-w-7xl mx-auto">
            <div className="lg:hidden w-10" /> {/* Spacer for mobile menu button */}
            <div className="flex items-center gap-4 ml-auto">
              <span className="text-sm text-muted-foreground hidden sm:block">
                {new Date().toLocaleDateString('es-MX', { 
                  weekday: 'long', 
                  year: 'numeric', 
                  month: 'long', 
                  day: 'numeric' 
                })}
              </span>
            </div>
          </div>
        </header>

        {/* Page content */}
        <div className="p-4 lg:p-6 max-w-7xl mx-auto">
          {children}
        </div>
      </main>
    </div>
  );
};

export default SidebarLayout;
